/**
 * WorkspaceHeader — Contextual Workspace Header with Flow Indicator (V107)
 *
 * Shows purpose statement, workspace context, and flow position.
 * Lightweight — no API calls, purely presentational.
 *
 * Props:
 *   workspace    — "sme" | "project" | "corporate" | "fi" | "states" | "admin"
 *   entityName   — org/user name to personalize
 *   subTitle     — optional subtitle (e.g. sector + country)
 *   stats        — optional [{label, value, color?}] quick stats
 *   showFlow     — whether to show the platform flow indicator (default true)
 */
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const WORKSPACE_META = {
  sme: {
    label:   "SME Workspace",
    purpose: "Measure your emissions, verify your data, and unlock financing opportunities.",
    accent:  "text-teal-400",
    bg:      "from-teal-900 to-[#0F172A]",
    position: 0,
  },
  project: {
    label:   "Project Workspace",
    purpose: "Assess climate impact and prepare your project for investment.",
    accent:  "text-blue-400",
    bg:      "from-blue-900 to-[#0F172A]",
    position: 1,
  },
  corporate: {
    label:   "Corporate Workspace",
    purpose: "Track emissions, identify reduction opportunities, and structure carbon value.",
    accent:  "text-indigo-400",
    bg:      "from-indigo-900 to-[#0F172A]",
    position: 2,
  },
  fi: {
    label:   "Financial Institution Workspace",
    purpose: "Evaluate, structure, and finance climate and circular economy opportunities.",
    accent:  "text-amber-400",
    bg:      "from-amber-900 to-[#0F172A]",
    position: 4,
  },
  states: {
    label:   "States Governance Workspace",
    purpose: "Aggregate climate actions and structure large-scale programmes for funding.",
    accent:  "text-purple-400",
    bg:      "from-purple-900 to-[#0F172A]",
    position: 3,
  },
  admin: {
    label:   "Platform Governance Workspace",
    purpose: "Govern all workspaces, users, verification, and institutional outputs.",
    accent:  "text-slate-400",
    bg:      "from-slate-800 to-[#0F172A]",
    position: -1,
  },
};

// Platform flow: how data moves through the system
const FLOW_NODES = [
  { id: "sme",       label: "SME",       short: "SME"       },
  { id: "project",   label: "Project",   short: "Project"   },
  { id: "corporate", label: "Corporate", short: "Corporate" },
  { id: "states",    label: "State",     short: "State"     },
  { id: "fi",        label: "FI",        short: "FI"        },
];

function FlowIndicator({ activeWorkspace }) {
  const [showTooltip, setShowTooltip] = useState(null);
  const activeIdx = FLOW_NODES.findIndex(n => n.id === activeWorkspace);

  return (
    <div className="hidden lg:flex items-center gap-0" data-testid="flow-indicator">
      {FLOW_NODES.map((node, i) => {
        const isActive = node.id === activeWorkspace;
        const isPast   = i < activeIdx;
        return (
          <React.Fragment key={node.id}>
            <div className="relative" onMouseEnter={() => setShowTooltip(i)} onMouseLeave={() => setShowTooltip(null)}>
              <div className={`px-2.5 py-1 rounded-full text-[8px] font-bold transition-all cursor-default ${
                isActive  ? "bg-white text-[#0F172A] shadow-sm" :
                isPast    ? "bg-white/20 text-white/70" :
                            "bg-white/5 text-white/30"
              }`}>
                {node.short}
              </div>
              {showTooltip === i && !isActive && (
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-black/80 text-white text-[7px] px-2 py-0.5 rounded whitespace-nowrap">
                  {node.label}
                </div>
              )}
            </div>
            {i < FLOW_NODES.length - 1 && (
              <div className={`w-4 h-px ${isPast || isActive ? "bg-white/30" : "bg-white/10"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function WorkspaceHeader({
  workspace,
  entityName,
  subTitle,
  stats = [],
  showFlow = true,
  children,
}) {
  const meta = WORKSPACE_META[workspace] || WORKSPACE_META.sme;

  return (
    <div className={`bg-gradient-to-br ${meta.bg} rounded-xl p-6`}
      data-testid={`workspace-header-${workspace}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          {/* Workspace label + purpose */}
          <p className={`text-[8px] font-bold uppercase tracking-widest mb-0.5 ${meta.accent} opacity-80`}>
            {meta.label}
          </p>
          <h1 className="text-xl font-bold text-white">
            {entityName || meta.label}
          </h1>
          {subTitle && (
            <p className="text-[9px] text-white/50 mt-0.5">{subTitle}</p>
          )}
          {/* Purpose statement — the "why" */}
          <p className="text-[9px] text-white/40 mt-2 max-w-md leading-relaxed italic">
            {meta.purpose}
          </p>
        </div>

        {/* Flow indicator */}
        {showFlow && workspace !== "admin" && (
          <FlowIndicator activeWorkspace={workspace} />
        )}
      </div>

      {/* Stats strip */}
      {stats.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-4">
          {stats.map((s, i) => (
            <div key={i} className="bg-white/5 rounded-xl px-3 py-2">
              <p className={`text-sm font-bold ${s.color || "text-teal-400"}`}>{s.value ?? "—"}</p>
              <p className="text-[7px] text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Optional children (e.g. StateSelector) */}
      {children && (
        <div className="mt-3">{children}</div>
      )}
    </div>
  );
}
